import {
  ChangeDetectionStrategy,
  Component,
  computed,
  inject,
  signal,
} from "@angular/core";
import { CommonModule } from "@angular/common";
import {
  MAT_DIALOG_DATA,
  MatDialogModule,
  MatDialogRef,
} from "@angular/material/dialog";
import { MatIcon } from "@angular/material/icon";
import { MatTooltip } from "@angular/material/tooltip";

import { SharedModule } from "../../../../shared/shared.module";
import { DialogHeaderExportComponent } from "../../../../shared/modules/dialog/dialog-header-export/dialog-header-export.component";
import { ChipsToggleComponent } from "../../../../shared/modules/chips-toggle/chips-toggle.component";
import { ChipsComponent } from "../../../../shared/modules/chips/chips.component";
import { RadioCardComponent } from "../../../../shared/components/radio-card/radio-card.component";
import { LotCardComponent } from "../../../../shared/components/lot-card/lot-card.component";
import { DateLimiteDisplayComponent } from "../../../../shared/components/date-limite-display/date-limite-display.component";
import { AutoSignificantDigitsPipe } from "../../../../shared/pipes/number-sig.pipe";
import { SnackService } from "../../../../shared/services/snack-service.service";

import {
  LOT_PAR_ETAT_DATA,
  LotParEtatDluoItem,
  LotParEtatNcItem,
} from "../../models/data.model";
import { exportLotParEtatExcel } from "./lot-par-etat-excel.utils";

export interface LotParEtatDialogData {
  data: LOT_PAR_ETAT_DATA;
}

type LotItem = LotParEtatDluoItem | LotParEtatNcItem;
type LotType = "ddm" | "dlc" | "dlcImminente" | "nc";
type GroupBy = "byArticle" | "byFamille" | "byEmplacement";

interface LotLine {
  lotId: string;
  numeroLot: string;
  articleId: string;
  articleName: string;
  articleCode: string | null;
  famille: string;
  sousFamille: string;
  emplacement: string;
  dluo?: string;
  dluoFormat?: string;
  typeDateLimite?: "DDM" | "DLC" | "AUCUNE";
  quantite: number;
  coutUnitaire: number;
  coutTotal: number;
}

interface ArticleSubRow {
  articleId: string;
  label: string;
  /** Référence article (code) — affichée en sous-titre du nom. */
  code: string | null;
  nbLots: number;
  qtyTotale: number;
  coutTotal: number;
  lots: LotLine[];
}

interface GroupRow {
  groupKey: string;
  label: string;
  /** Optionnel pour les groupes par article : nom de la famille parente */
  meta?: string;
  nbLots: number;
  qtyTotale: number;
  coutTotal: number;
  /** Articles à l'intérieur du groupe (mode byFamille / byEmplacement).
   *  En mode byArticle ce tableau a 1 seul élément = l'article lui-même. */
  articles: ArticleSubRow[];
}

const UNCLASSIFIED = "— Non classé —";

/**
 * Dialog "Voir tout" du widget Lots par état.
 *
 * 2 modes (toggle DLUO / NC), 3 pivots (par Article / Famille / Emplacement),
 * 4 stats cards, arbre expandable group → lots avec tri par coût total décr.
 * Export Excel (3 feuilles : Résumé / DLUO / NC) via `exportLotParEtatExcel`.
 *
 * Le dialog reçoit la data telle quelle (pas de re-fetch back) — il consomme
 * le même cache widget que le composant compact pour rester cohérent.
 */
@Component({
  selector: "vl-lot-par-etat-dialog",
  standalone: true,
  imports: [
    CommonModule,
    MatDialogModule,
    MatIcon,
    MatTooltip,
    SharedModule,
    DialogHeaderExportComponent,
    ChipsToggleComponent,
    ChipsComponent,
    RadioCardComponent,
    LotCardComponent,
    DateLimiteDisplayComponent,
    AutoSignificantDigitsPipe,
  ],
  templateUrl: "./lot-par-etat-dialog.component.html",
  styleUrl: "./lot-par-etat-dialog.component.scss",
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class LotParEtatDialogComponent {
  data = inject<LotParEtatDialogData>(MAT_DIALOG_DATA);
  dialogRef = inject(MatDialogRef<LotParEtatDialogComponent>);
  private snack = inject(SnackService);

  // ─── Toggles UI ────────────────────────────────────────────────────────
  lotType = signal<LotType>("ddm");
  lotTypeOptions = [
    { name: "DDM dépassées", value: "ddm" as const },
    { name: "DLC dépassées", value: "dlc" as const },
    { name: "DLC imminentes (J-7)", value: "dlcImminente" as const },
    { name: "Lots bloqués (NC)", value: "nc" as const },
  ];

  groupBy = signal<GroupBy>("byFamille");
  groupByOptions = [
    { name: "Par famille", value: "byFamille" as const },
    { name: "Par article", value: "byArticle" as const },
    { name: "Par entrepôt", value: "byEmplacement" as const },
  ];

  /** Set des keys de groupes dépliés. */
  expanded = signal<Set<string>>(new Set());

  toggleGroup(key: string): void {
    this.expanded.update((cur) => {
      const next = new Set(cur);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });
  }

  isExpanded(key: string): boolean {
    return this.expanded().has(key);
  }

  expandAll(): void {
    this.expanded.set(new Set(this.groups().map((g) => g.groupKey)));
  }

  collapseAll(): void {
    this.expanded.set(new Set());
  }

  // ─── Conversion items → lignes plates ──────────────────────────────────
  /**
   * Aplatit la liste d'items (1 lot = N stocks) en une liste de lignes
   * (1 stock = 1 ligne), normalisées pour pivoter facilement.
   */
  flatLines = computed<LotLine[]>(() => {
    const sel = this.lotType();
    let items: LotItem[];
    switch (sel) {
      case "ddm":
        items = this.data.data.dluo.filter(
          (it) => it.typeDateLimite !== "DLC",
        );
        break;
      case "dlc":
        items = this.data.data.dluo.filter(
          (it) => it.typeDateLimite === "DLC",
        );
        break;
      case "dlcImminente":
        items = this.data.data.dlcImminente ?? [];
        break;
      case "nc":
        items = this.data.data.nc;
        break;
    }
    const isDluo = sel !== "nc"; // DDM / DLC / DLC imminente → tous ont une DLUO
    const out: LotLine[] = [];
    for (const it of items) {
      const fam = it.famille?.trim() || UNCLASSIFIED;
      const sub = it.sousFamille?.trim() || UNCLASSIFIED;
      for (const st of it.stocks ?? []) {
        out.push({
          lotId: it.lotId,
          numeroLot: it.numeroLot,
          articleId: it.articleId,
          articleName: it.articleDenomination ?? "—",
          articleCode: it.articleCode ?? null,
          famille: fam,
          sousFamille: sub,
          emplacement: st.adresseStockDenomination ?? "—",
          dluo: isDluo ? (it as LotParEtatDluoItem).dluoString : undefined,
          dluoFormat: isDluo
            ? (it as LotParEtatDluoItem).dluoFormat
            : undefined,
          typeDateLimite: isDluo
            ? (it as LotParEtatDluoItem).typeDateLimite
            : undefined,
          quantite: st.quantite ?? 0,
          coutUnitaire: st.coutUnitaire ?? it.coutUnitaire ?? 0,
          coutTotal:
            (st.quantite ?? 0) * (st.coutUnitaire ?? it.coutUnitaire ?? 0),
        });
      }
    }
    return out;
  });

  // ─── Groupes selon `groupBy` ───────────────────────────────────────────
  groups = computed<GroupRow[]>(() => {
    const lines = this.flatLines();
    const groupBy = this.groupBy();
    const map = new Map<string, GroupRow>();
    // Bucket à 2 niveaux : groupKey → articleId → lines
    type Bucket = Map<string, Map<string, LotLine[]>>;
    const buckets: Bucket = new Map();
    const labels = new Map<string, { label: string; meta?: string }>();
    const articleLabels = new Map<string, { name: string; code: string | null }>();

    for (const l of lines) {
      let key: string;
      let label: string;
      let meta: string | undefined;
      if (groupBy === "byFamille") {
        key = l.famille;
        label = l.famille;
      } else if (groupBy === "byArticle") {
        key = l.articleId;
        label = l.articleName;
        meta = l.famille;
      } else {
        key = l.emplacement;
        label = l.emplacement;
      }
      labels.set(key, { label, meta });
      articleLabels.set(l.articleId, { name: l.articleName, code: l.articleCode });
      const articlesMap = buckets.get(key) ?? new Map<string, LotLine[]>();
      const list = articlesMap.get(l.articleId) ?? [];
      list.push(l);
      articlesMap.set(l.articleId, list);
      buckets.set(key, articlesMap);
    }

    const groups: GroupRow[] = [];
    for (const [key, articlesMap] of buckets) {
      const articles: ArticleSubRow[] = [];
      let groupQty = 0;
      let groupCout = 0;
      const groupLotIds = new Set<string>();

      for (const [articleId, lots] of articlesMap) {
        const lotIds = new Set<string>();
        let qty = 0;
        let cout = 0;
        for (const l of lots) {
          lotIds.add(l.lotId);
          groupLotIds.add(l.lotId);
          qty += l.quantite;
          cout += l.coutTotal;
        }
        groupQty += qty;
        groupCout += cout;
        const info = articleLabels.get(articleId);
        articles.push({
          articleId,
          label: info?.name ?? articleId,
          code: info?.code ?? null,
          nbLots: lotIds.size,
          qtyTotale: qty,
          coutTotal: cout,
          lots: lots.slice().sort((a, b) => b.coutTotal - a.coutTotal),
        });
      }
      articles.sort((a, b) => b.coutTotal - a.coutTotal);

      const meta = labels.get(key);
      groups.push({
        groupKey: key,
        label: meta?.label ?? key,
        meta: meta?.meta,
        nbLots: groupLotIds.size,
        qtyTotale: groupQty,
        coutTotal: groupCout,
        articles,
      });
    }
    return groups.sort((a, b) => b.coutTotal - a.coutTotal);
  });

  // ─── Stats globales ─────────────────────────────────────────────────────
  stats = computed(() => {
    const sel = this.lotType();
    let items: LotItem[];
    switch (sel) {
      case "ddm":
        items = this.data.data.dluo.filter(
          (it) => it.typeDateLimite !== "DLC",
        );
        break;
      case "dlc":
        items = this.data.data.dluo.filter(
          (it) => it.typeDateLimite === "DLC",
        );
        break;
      case "dlcImminente":
        items = this.data.data.dlcImminente ?? [];
        break;
      case "nc":
        items = this.data.data.nc;
        break;
    }
    const nbLots = items.length;
    const coutTotal = items.reduce((s, it) => s + (it.coutTotal ?? 0), 0);
    const qtyTotale = items.reduce(
      (s, it) =>
        s + (it.stocks ?? []).reduce((q, st) => q + (st.quantite ?? 0), 0),
      0,
    );
    const top = this.groups()[0];
    return {
      nbLots,
      coutTotal,
      qtyTotale,
      topLabel: top?.label ?? "—",
      topShare:
        top && coutTotal > 0 ? (top.coutTotal / coutTotal) * 100 : 0,
    };
  });

  // ─── Export Excel ──────────────────────────────────────────────────────
  exporting = signal(false);
  async exportExcel(): Promise<void> {
    if (this.exporting()) return;
    this.exporting.set(true);
    try {
      await exportLotParEtatExcel(this.data.data);
      this.snack.success("Export Excel téléchargé");
    } catch (e) {
      console.error("[lot-par-etat] export Excel failed", e);
      this.snack.error("Échec de l'export Excel");
    } finally {
      this.exporting.set(false);
    }
  }

  /** Libellé de la 1ʳᵉ colonne du tableau selon le mode pivot courant. */
  groupColumnLabel = computed(() => {
    switch (this.groupBy()) {
      case "byFamille":
        return "Famille";
      case "byArticle":
        return "Article";
      case "byEmplacement":
        return "Entrepôt";
    }
  });

  /** Titre de la stat card "Top" selon le pivot. */
  topCardTitle = computed(() => {
    switch (this.groupBy()) {
      case "byFamille":
        return "Top famille";
      case "byArticle":
        return "Top article";
      case "byEmplacement":
        return "Top entrepôt";
    }
  });

  /** Part en % d'une valeur sur le total de la vue courante. */
  partPct(value: number): number {
    const total = this.stats().coutTotal;
    if (total <= 0) return 0;
    return (value / total) * 100;
  }

  // ─── Palette pour les bordures de groupe ───────────────────────────────
  private static readonly PALETTE = [
    "#4e79a7", "#f28e2b", "#e15759", "#76b7b2", "#59a14f",
    "#edc948", "#b07aa1", "#ff9da7", "#9c755f", "#bab0ac",
  ];

  /** Couleur stable basée sur la position du groupe dans le top trié. */
  getColor(groupKey: string): string {
    const idx = this.groups().findIndex((g) => g.groupKey === groupKey);
    if (idx < 0) return "var(--color-outline)";
    return LotParEtatDialogComponent.PALETTE[
      idx % LotParEtatDialogComponent.PALETTE.length
    ];
  }

}
